import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, FileText, Plus, Trash2, Eye, Send, Check, ChevronDown, Download, Loader2, Search } from "lucide-react";
import { BRAND } from "@/lib/brand";

const LOGO_URL = BRAND.logos.wordmarkBlue;
const EMBLEM_URL = BRAND.logos.emblemBlue;

// Fallback templates if none uploaded
const FALLBACK_TEMPLATES = [
  { id: "standard", template_name: "Standard Service Agreement", original_filename: "Standard", section_count: 6, sections: [
    { title: "Services", content: "Provider agrees to deliver the following healthcare technology services as specified in the line items below." },
    { title: "Term", content: "This Agreement shall commence on the Start Date and continue for an initial term of twelve (12) months, automatically renewing for successive 12-month periods unless either party provides 30 days' written notice." },
    { title: "Payment Terms", content: "Client shall pay all fees within thirty (30) days of invoice date. Late payments accrue interest at 1.5% per month. All prices are in USD." },
    { title: "Service Level Agreement", content: "Provider guarantees 99.9% uptime for all cloud-hosted services. Scheduled maintenance windows: Sundays 2-6 AM ET with 48-hour advance notice." },
    { title: "Confidentiality & HIPAA", content: "Both parties agree to maintain confidentiality of all proprietary information. Provider shall comply with HIPAA requirements and execute a Business Associate Agreement (BAA)." },
    { title: "Termination", content: "Either party may terminate with 30 days' written notice. Early termination by Client incurs remaining contract balance. Provider may terminate immediately for non-payment exceeding 60 days." },
  ]},
];

export default function ContractBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const quoteId = searchParams.get("from_quote");

  const [step, setStep] = useState(quoteId ? 2 : 1);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [title, setTitle] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [leadTitle, setLeadTitle] = useState("");
  const [leadId, setLeadId] = useState("");
  const [lineItems, setLineItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [total, setTotal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [tplSearch, setTplSearch] = useState("");

  useEffect(() => {
    fetchTemplates();
    if (quoteId) loadFromQuote(quoteId);
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- all helpers defined below and stable; run once on mount/quoteId
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/contract-templates`, { headers: { Authorization: `Bearer ${token}` } });
      const parsed = (res.data || []).filter(t => t.parsed && t.sections?.length > 0);
      setTemplates(parsed.length > 0 ? parsed : FALLBACK_TEMPLATES);
    } catch { setTemplates(FALLBACK_TEMPLATES); }
    setLoadingTemplates(false);
  };

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((res.data || []).filter(q => q.status === "accepted" || q.status === "sent"));
    } catch {}
  };

  const loadFromQuote = async (qId) => {
    try {
      const res = await axios.get(`${API}/quotes/${qId}`, { headers: { Authorization: `Bearer ${token}` } });
      const q = res.data;
      setSelectedQuote(q);
      setTitle(`Service Agreement — ${q.contact_company || q.lead_title || q.title}`);
      setContactName(q.contact_name || "");
      setContactEmail(q.contact_email || "");
      setLeadTitle(q.lead_title || q.contact_company || "");
      setLeadId(q.lead_id || "");
      setLineItems(q.line_items || []);
      setTotal(q.total || 0);
      setStep(2);
    } catch { toast.error("Failed to load quote"); }
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setSections((tpl.sections || []).map(s => ({ ...s })));
    if (!title) setTitle(tpl.template_name || tpl.original_filename?.replace(".docx", "") || "Service Agreement");
    setStep(2);
  };

  const loadQuoteIntoContract = (q) => {
    setSelectedQuote(q);
    setTitle(`Service Agreement — ${q.contact_company || q.lead_title || q.title}`);
    setContactName(q.contact_name || "");
    setContactEmail(q.contact_email || "");
    setLeadTitle(q.lead_title || q.contact_company || "");
    setLeadId(q.lead_id || "");
    setLineItems(q.line_items || []);
    setTotal(q.total || 0);
  };

  const addLineItem = () => setLineItems(prev => [...prev, { product: "", description: "", unit_price: 0, quantity: 1, total: 0 }]);
  const updateLineItem = (i, field, value) => {
    setLineItems(prev => {
      const items = [...prev];
      items[i] = { ...items[i], [field]: value };
      if (field === "unit_price" || field === "quantity") items[i].total = (items[i].unit_price || 0) * (items[i].quantity || 1);
      return items;
    });
  };
  const removeLineItem = (i) => setLineItems(prev => prev.filter((_, j) => j !== i));
  useEffect(() => { setTotal(lineItems.reduce((s, it) => s + (it.total || it.unit_price || 0), 0)); }, [lineItems]);
  const updateSection = (i, content) => setSections(prev => { const s = [...prev]; s[i] = { ...s[i], content }; return s; });
  const addSection = () => setSections(prev => [...prev, { title: "New Section", content: "" }]);
  const removeSection = (i) => setSections(prev => prev.filter((_, j) => j !== i));

  const saveAndSend = async (action = "draft") => {
    if (!title) { toast.error("Contract title is required"); return; }
    setSaving(true);
    try {
      const terms = sections.map(s => `${s.title}\n${s.content}`).join("\n\n");
      const res = await axios.post(`${API}/contracts`, {
        title, line_items: lineItems, total, terms, lead_id: leadId,
        contact_id: "", contact_name: contactName, contact_company: leadTitle,
        start_date: startDate, notes: "",
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (action === "submit") {
        await axios.post(`${API}/contracts/${res.data.id}/submit-for-approval`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Contract submitted for approval");
      } else {
        toast.success(`Contract ${res.data.contract_number} saved as draft`);
      }
      navigate("/quotes/list");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const downloadPdf = () => {
    const w = window.open("", "_blank");
    const items = lineItems.map(li => `<div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee;font-size:13px"><span>${li.product || "Service"}</span><span>$${(li.total || li.unit_price || 0).toLocaleString()}</span></div>`).join("");
    const terms = sections.map(s => `<div style="margin-bottom:20px"><div style="font-size:13px;font-weight:600;color:#111;margin-bottom:6px">${s.title}</div><div style="font-size:11.5px;color:#333;line-height:1.7;white-space:pre-wrap">${(s.content || "").replace(/</g, "&lt;")}</div></div>`).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>
      @page{margin:0.6in 0.75in}body{font-family:Helvetica,Arial,sans-serif;color:#111;margin:0;padding:0}
      .letterhead{display:flex;justify-content:space-between;align-items:center;padding:24px 0;border-bottom:2px solid #1B365D;margin-bottom:24px}
      .logo{height:38px}.co-info{text-align:right;font-size:10px;color:#555;line-height:1.6}
      .title{font-size:20px;font-weight:600;color:#1B365D;margin-bottom:2px}
      .subtitle{font-size:11px;color:#888;margin-bottom:20px}
      .parties{display:flex;gap:40px;margin-bottom:24px;padding:16px 0;border-bottom:1px solid #eee}
      .party-label{font-size:9px;text-transform:uppercase;color:#888;letter-spacing:0.5px;margin-bottom:4px}
      .party-name{font-size:14px;font-weight:600}
      .items-header{display:flex;justify-content:space-between;padding:8px 0;border-bottom:2px solid #111;font-size:10px;font-weight:700;text-transform:uppercase;color:#555}
      .total-row{display:flex;justify-content:flex-end;padding:12px 0;margin-top:4px;font-size:16px;font-weight:700;color:#1a7a52}
      .sig-area{margin-top:40px;display:flex;gap:40px}.sig-block{flex:1;border-top:1px solid #888;padding-top:8px}
      .sig-label{font-size:10px;color:#888;text-transform:uppercase}.sig-line{height:32px}
      .footer{margin-top:40px;text-align:center;font-size:9px;color:#aaa;padding-top:12px;border-top:1px solid #eee}
      @media print{body{padding:0}}
    </style></head><body style="padding:40px 50px">
      <div class="letterhead">
        <img src="${LOGO_URL}" class="logo" crossorigin="anonymous" onerror="this.style.display='none'" />
        <div class="co-info">Enable Healthcare, Inc.<br>225 Brae Blvd, Suite 125<br>Park Ridge, NJ 07656<br>(973) 520-6000</div>
      </div>
      <div class="title">${title || "Service Agreement"}</div>
      <div class="subtitle">Date: ${startDate ? new Date(startDate + "T00:00:00").toLocaleDateString("en-US", {month:"long",day:"numeric",year:"numeric"}) : new Date().toLocaleDateString("en-US", {month:"long",day:"numeric",year:"numeric"})}</div>
      <div class="parties">
        <div><div class="party-label">Service Provider</div><div class="party-name">Enable Healthcare, Inc.</div><div style="font-size:11px;color:#555">225 Brae Blvd, Suite 125<br>Park Ridge, NJ 07656</div></div>
        <div><div class="party-label">Client</div><div class="party-name">${contactName || "___________________"}</div><div style="font-size:11px;color:#555">${leadTitle || "___________________"}</div></div>
      </div>
      <div style="margin-bottom:24px">
        <div class="items-header"><span>Product / Service</span><span>Amount</span></div>
        ${items}
        <div class="total-row">Total: $${total.toLocaleString()}/mo</div>
      </div>
      <div style="margin-bottom:20px">${terms}</div>
      <div class="sig-area">
        <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Enable Healthcare, Inc. — Authorized Signatory</div><div style="font-size:10px;color:#888;margin-top:4px">Date: _______________</div></div>
        <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Client — Authorized Signatory</div><div style="font-size:10px;color:#888;margin-top:4px">Date: _______________</div></div>
      </div>
      <div class="footer">Enable Healthcare, Inc. · Confidential · Generated ${new Date().toLocaleDateString()}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const filteredTemplates = templates.filter(t => {
    if (!tplSearch) return true;
    return (t.template_name || t.original_filename || "").toLowerCase().includes(tplSearch.toLowerCase());
  });

  // Group templates by category
  const categories = {};
  filteredTemplates.forEach(t => {
    const name = t.template_name || "";
    let cat = "Other";
    if (name.match(/EMR|MDnet|PMS/i)) cat = "EHR & Practice Management";
    else if (name.match(/RCM|Revenue|Billing|RevQ|AR Recovery/i)) cat = "Revenue Cycle";
    else if (name.match(/CCM|RPM|Care Coord|APCM|Clinical Staffing/i)) cat = "Care Coordination";
    else if (name.match(/Migration|Exit/i)) cat = "Data & Migration";
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(t);
  });

  return (
    <div data-testid="contract-builder-page" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 52px)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderBottom: "0.5px solid var(--border)", background: "var(--surface)", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate(-1)} className="btn" style={{ padding: "4px 8px", fontSize: 11 }} data-testid="contract-back"><ArrowLeft style={{ width: 14, height: 14 }} /> Back</button>
          {step > 1 && <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Contract title..." style={{ fontSize: 16, fontWeight: 700, border: "none", outline: "none", background: "transparent", width: 400, fontFamily: "var(--font)" }} data-testid="contract-title" />}
        </div>
        {step > 1 && (
          <div style={{ display: "flex", gap: 8 }}>
            {step === 2 && <button onClick={() => setStep(3)} className="btn" style={{ fontSize: 11, padding: "6px 14px" }} data-testid="preview-contract"><Eye style={{ width: 12, height: 12 }} /> Preview</button>}
            {step === 3 && <button onClick={() => setStep(2)} className="btn" style={{ fontSize: 11, padding: "6px 14px" }}><ArrowLeft style={{ width: 12, height: 12 }} /> Edit</button>}
            {step === 3 && <button onClick={downloadPdf} className="btn" style={{ fontSize: 11, padding: "6px 14px" }}><Download style={{ width: 12, height: 12 }} /> Download PDF</button>}
            <button onClick={() => saveAndSend("draft")} disabled={saving} className="btn" style={{ fontSize: 11, padding: "6px 14px" }} data-testid="save-contract-draft">Save Draft</button>
            <button onClick={() => saveAndSend("submit")} disabled={saving} className="btn btn-primary" style={{ fontSize: 11, padding: "6px 14px" }} data-testid="send-contract"><Send style={{ width: 12, height: 12 }} /> Submit for Approval</button>
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        {/* Step 1: Template Selection */}
        {step === 1 && (
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Create a new contract</div>
            <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginBottom: 24 }}>Choose from your uploaded agreement templates or base it on an accepted quote</div>

            {/* From Quote */}
            {quotes.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>Generate from accepted quote</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {quotes.slice(0, 5).map(q => (
                    <div key={q.id} onClick={() => { loadQuoteIntoContract(q); selectTemplate(templates[0] || FALLBACK_TEMPLATES[0]); }} data-testid={`from-quote-${q.id}`}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", border: "0.5px solid var(--border)", borderRadius: 10, cursor: "pointer", background: "var(--surface)", transition: "border-color 0.15s" }}
                      onMouseOver={e => e.currentTarget.style.borderColor = "var(--blue)"} onMouseOut={e => e.currentTarget.style.borderColor = "var(--border)"}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{q.quote_number} — {q.title}</div>
                        <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 2 }}>{q.contact_name} · {q.contact_company} · {(q.line_items || []).length} items</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--green)" }}>${(q.total || 0).toLocaleString()}/mo</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.4px" }}>Agreement templates ({filteredTemplates.length})</div>
              <div style={{ position: "relative" }}>
                <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "var(--text-tertiary)" }} />
                <input value={tplSearch} onChange={e => setTplSearch(e.target.value)} placeholder="Search templates..." style={{ paddingLeft: 28, padding: "6px 10px 6px 28px", border: "0.5px solid var(--border)", borderRadius: 8, fontSize: 12, fontFamily: "var(--font)", width: 200 }} />
              </div>
            </div>

            {loadingTemplates ? <div style={{ padding: 32, textAlign: "center" }}><Loader2 style={{ width: 20, height: 20, animation: "spin 0.8s linear infinite", color: "var(--blue)" }} /></div> :
              Object.entries(categories).map(([cat, tpls]) => (
                <div key={cat} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8, paddingBottom: 4, borderBottom: "0.5px solid var(--border)" }}>{cat}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {tpls.map(tpl => (
                      <div key={tpl.id} onClick={() => selectTemplate(tpl)} data-testid={`template-${tpl.id}`}
                        style={{ padding: "14px 16px", border: "0.5px solid var(--border)", borderRadius: 10, cursor: "pointer", background: "var(--surface)", transition: "border-color 0.15s, box-shadow 0.15s", display: "flex", gap: 12, alignItems: "flex-start" }}
                        onMouseOver={e => { e.currentTarget.style.borderColor = "var(--blue)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)"; }}
                        onMouseOut={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "none"; }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <FileText style={{ width: 16, height: 16, color: "var(--blue)" }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>{tpl.template_name || tpl.original_filename?.replace(".docx", "")}</div>
                          <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{tpl.section_count || tpl.sections?.length || 0} sections · {tpl.paragraph_count || "—"} paragraphs</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {/* Step 2: Editor */}
        {step === 2 && (
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div><label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Client Name</label><input className="input" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Contact name..." data-testid="contract-contact" /></div>
              <div><label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Practice / Company</label><input className="input" value={leadTitle} onChange={e => setLeadTitle(e.target.value)} placeholder="Practice name..." /></div>
              <div><label style={{ fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Start Date</label><input type="date" className="input" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>Products & Services</div>
            <div style={{ border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 60px 80px 32px", gap: 0, padding: "8px 12px", background: "var(--page-bg)", fontSize: 9, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase" }}><span>Product</span><span>Description</span><span>Price</span><span>Qty</span><span>Total</span><span></span></div>
              {lineItems.map((item, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 60px 80px 32px", gap: 8, padding: "8px 12px", borderTop: "0.5px solid var(--border)", alignItems: "center" }}>
                  <input value={item.product || ""} onChange={e => updateLineItem(i, "product", e.target.value)} className="input" style={{ fontSize: 12 }} placeholder="Product name" data-testid={`li-product-${i}`} />
                  <input value={item.description || ""} onChange={e => updateLineItem(i, "description", e.target.value)} className="input" style={{ fontSize: 11 }} placeholder="Description" />
                  <input type="number" value={item.unit_price || 0} onChange={e => updateLineItem(i, "unit_price", parseFloat(e.target.value) || 0)} className="input" style={{ fontSize: 12, textAlign: "right" }} />
                  <input type="number" value={item.quantity || 1} onChange={e => updateLineItem(i, "quantity", parseInt(e.target.value) || 1)} className="input" style={{ fontSize: 12, textAlign: "center" }} />
                  <div style={{ fontSize: 12, fontWeight: 600, textAlign: "right" }}>${(item.total || item.unit_price || 0).toLocaleString()}</div>
                  <button onClick={() => removeLineItem(i)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}><Trash2 style={{ width: 12, height: 12, color: "var(--red)" }} /></button>
                </div>
              ))}
              <div style={{ padding: "8px 12px", borderTop: "0.5px solid var(--border)" }}>
                <button onClick={addLineItem} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--blue)", fontWeight: 600, fontFamily: "var(--font)" }} data-testid="add-line-item"><Plus style={{ width: 12, height: 12 }} /> Add item</button>
              </div>
              <div style={{ padding: "10px 12px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", background: "var(--page-bg)" }}>
                <span style={{ fontSize: 14, fontWeight: 800 }}>Total: ${total.toLocaleString()}/mo</span>
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 }}>Contract Terms & Sections</div>
            {sections.map((sec, i) => (
              <div key={i} style={{ border: "0.5px solid var(--border)", borderRadius: 10, padding: 14, marginBottom: 8, background: "var(--surface)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <input value={sec.title} onChange={e => { const s = [...sections]; s[i] = { ...s[i], title: e.target.value }; setSections(s); }} style={{ fontSize: 13, fontWeight: 650, border: "none", outline: "none", background: "transparent", fontFamily: "var(--font)", flex: 1 }} />
                  <button onClick={() => removeSection(i)} style={{ background: "none", border: "none", cursor: "pointer" }}><Trash2 style={{ width: 12, height: 12, color: "var(--text-tertiary)" }} /></button>
                </div>
                <textarea value={sec.content} onChange={e => updateSection(i, e.target.value)} rows={4} style={{ width: "100%", fontSize: 12, border: "0.5px solid var(--border)", borderRadius: 8, padding: 8, fontFamily: "var(--font)", resize: "vertical", outline: "none", lineHeight: 1.6 }} data-testid={`section-${i}`} />
              </div>
            ))}
            <button onClick={addSection} className="btn" style={{ fontSize: 11 }} data-testid="add-section"><Plus style={{ width: 12, height: 12 }} /> Add section</button>
          </div>
        )}

        {/* Step 3: Preview with Letterhead */}
        {step === 3 && (
          <div style={{ maxWidth: 720, margin: "0 auto", background: "#fff", border: "0.5px solid #eee", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 20px rgba(0,0,0,0.04)" }}>
            {/* Letterhead */}
            <div style={{ padding: "24px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #1B365D" }}>
              <img src={LOGO_URL} alt="Enable Healthcare" style={{ height: 36 }} onError={e => e.target.style.display = "none"} />
              <div style={{ textAlign: "right", fontSize: 10, color: "#555", lineHeight: 1.6 }}>
                Enable Healthcare, Inc.<br />
                225 Brae Blvd, Suite 125<br />
                Park Ridge, NJ 07656<br />
                (973) 520-6000
              </div>
            </div>
            {/* Title */}
            <div style={{ padding: "20px 32px", borderBottom: "0.5px solid #eee" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1B365D" }}>{title}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Date: {startDate ? new Date(startDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
            </div>
            {/* Parties */}
            <div style={{ padding: "16px 32px", borderBottom: "0.5px solid #eee", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div><div style={{ fontSize: 9, textTransform: "uppercase", color: "#888", letterSpacing: "0.5px", marginBottom: 4 }}>Service Provider</div><div style={{ fontSize: 14, fontWeight: 600 }}>Enable Healthcare, Inc.</div><div style={{ fontSize: 11, color: "#555" }}>225 Brae Blvd, Suite 125<br />Park Ridge, NJ 07656</div></div>
              <div><div style={{ fontSize: 9, textTransform: "uppercase", color: "#888", letterSpacing: "0.5px", marginBottom: 4 }}>Client</div><div style={{ fontSize: 14, fontWeight: 600 }}>{contactName || "___________________"}</div><div style={{ fontSize: 11, color: "#555" }}>{leadTitle || "___________________"}</div></div>
            </div>
            {/* Line Items */}
            <div style={{ padding: "16px 32px", borderBottom: "0.5px solid #eee" }}>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "2px solid #111", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#555" }}><span>Product / Service</span><span>Amount</span></div>
              {lineItems.map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "0.5px solid #f5f5f5" }}>
                  <div><div style={{ fontSize: 13, fontWeight: 500 }}>{item.product}</div>{item.description && <div style={{ fontSize: 11, color: "#888" }}>{item.description}</div>}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>${(item.total || item.unit_price || 0).toLocaleString()}/mo</div>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 0 0", marginTop: 4 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#1a7a52" }}>Total: ${total.toLocaleString()}/mo</span>
              </div>
            </div>
            {/* Terms */}
            {sections.map((sec, i) => (
              <div key={i} style={{ padding: "14px 32px", borderBottom: "0.5px solid #eee" }}>
                <div style={{ fontSize: 13, fontWeight: 650, color: "#111", marginBottom: 4 }}>{sec.title}</div>
                <div style={{ fontSize: 11.5, color: "#333", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{sec.content}</div>
              </div>
            ))}
            {/* Signature */}
            <div style={{ padding: "24px 32px" }}>
              <div style={{ display: "flex", gap: 40 }}>
                <div style={{ flex: 1 }}><div style={{ height: 32 }} /><div style={{ borderTop: "1px solid #888", paddingTop: 8, fontSize: 10, color: "#888", textTransform: "uppercase" }}>Enable Healthcare, Inc. — Authorized Signatory</div><div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>Date: _______________</div></div>
                <div style={{ flex: 1 }}><div style={{ height: 32 }} /><div style={{ borderTop: "1px solid #888", paddingTop: 8, fontSize: 10, color: "#888", textTransform: "uppercase" }}>Client — Authorized Signatory</div><div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>Date: _______________</div></div>
              </div>
            </div>
            {/* Footer */}
            <div style={{ padding: "12px 32px", borderTop: "0.5px solid #eee", textAlign: "center", fontSize: 9, color: "#aaa" }}>
              Enable Healthcare, Inc. · Confidential · Generated {new Date().toLocaleDateString()}
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
